// Formulario genérico dirigido por FieldDef[]. Renderiza por tipo, valida
// requeridos y coacciona tipos antes de enviar (omite vacíos opcionales).
import type { JSX } from "react";
import { useEffect, useMemo, useState } from "react";
import type { FieldDef } from "../modules/registry.tsx";
import { sectionIcon } from "../lib/sectionIcons.ts";
import { Field, Input, Select, Textarea } from "./ui.tsx";
import { LocalCombobox } from "./LocalCombobox.tsx";
import { BusinessUnitSelect } from "./BusinessUnitSelect.tsx";

export type Values = Record<string, unknown>;

function groupBySection(fields: FieldDef[]): Array<[string, FieldDef[]]> {
  const map = new Map<string, FieldDef[]>();
  for (const f of fields) {
    const s = f.section ?? "General";
    const arr = map.get(s) ?? [];
    arr.push(f);
    map.set(s, arr);
  }
  return [...map.entries()];
}

/** Coacciona y limpia según los tipos; omite opcionales vacíos.
 *  Los campos personalizados (f.custom) se agrupan bajo `customData`. */
export function buildPayload(fields: FieldDef[], values: Values): Values {
  const out: Values = {};
  const custom: Values = {};
  for (const f of fields) {
    const target = f.custom ? custom : out;
    const raw = values[f.name];
    if (f.type === "bool") {
      target[f.name] = Boolean(raw);
      continue;
    }
    // Unidad de negocio: enviar null cuando se deja vacío (permite desasignar).
    if (f.type === "businessunit") {
      target[f.name] = raw ? raw : null;
      continue;
    }
    if (raw === undefined || raw === null || raw === "") continue;
    if (f.type === "int" || f.type === "currency" || f.type === "decimal" || f.type === "percent") {
      const n = Number(raw);
      if (!Number.isNaN(n)) target[f.name] = n;
    } else {
      target[f.name] = raw;
    }
  }
  if (Object.keys(custom).length > 0) out["customData"] = custom;
  return out;
}

export function validate(fields: FieldDef[], values: Values): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const f of fields) {
    if (f.required) {
      const v = values[f.name];
      if (v === undefined || v === null || String(v).trim() === "") {
        errors[f.name] = "Este campo es obligatorio.";
      }
    }
    if (f.type === "email" && values[f.name]) {
      const s = String(values[f.name]);
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) errors[f.name] = "Ingresa un correo válido.";
    }
  }
  return errors;
}

export function RecordForm(
  { fields, initial, errors, onChange }:
  { fields: FieldDef[]; initial: Values; errors: Record<string, string>; onChange: (v: Values) => void },
): JSX.Element {
  const [values, setValues] = useState<Values>(initial);
  const [shake, setShake] = useState(false);
  const sections = useMemo(() => groupBySection(fields), [fields]);

  // Shake al enviar con errores (solo al validar, no mientras escribe).
  useEffect(() => {
    if (Object.keys(errors).length > 0) { setShake(true); const t = setTimeout(() => setShake(false), 320); return () => clearTimeout(t); }
    return undefined;
  }, [errors]);

  const set = (name: string, v: unknown): void => {
    const next = { ...values, [name]: v };
    setValues(next);
    onChange(next);
  };

  return (
    <div className={shake ? "shake" : ""}>
    <div className="space-y-5 stagger">
      {sections.map(([section, sfields]) => {
        const Icon = sectionIcon(section);
        return (
          <section key={section} className="rounded-xl border border-line bg-canvas/40 p-4">
            <div className="flex items-center gap-2.5 mb-4">
              <span className="w-7 h-7 rounded-lg grid place-items-center bg-primary/[0.08] text-primary shrink-0">
                <Icon size={15} strokeWidth={1.75} />
              </span>
              <h4 className="text-sm font-semibold text-ink tracking-tight">{section}</h4>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
              {sfields.map((f) => (
                <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                  <FieldInput
                    field={f}
                    value={values[f.name]}
                    error={errors[f.name]}
                    onChange={(v) => set(f.name, v)}
                  />
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
    </div>
  );
}

function FieldInput(
  { field, value, error, onChange }:
  { field: FieldDef; value: unknown; error?: string | undefined; onChange: (v: unknown) => void },
): JSX.Element {
  const common = { required: field.required };
  if (field.type === "bool") {
    return (
      <label className="flex items-center gap-2 mt-6 text-sm text-ink-secondary">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="rounded border-line"
        />
        {field.label}
      </label>
    );
  }
  return (
    <Field label={field.label} required={field.required} {...(error ? { error } : {})}>
      {field.type === "textarea" ? (
        <Textarea rows={3} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />
      ) : field.type === "businessunit" ? (
        <BusinessUnitSelect value={String(value ?? "")} onChange={(v) => onChange(v)} />
      ) : field.type === "enum" ? (
        (field.options?.length ?? 0) > 25 ? (
          <LocalCombobox
            value={String(value ?? "")}
            options={field.options ?? []}
            onChange={(v) => onChange(v)}
            placeholder={`Buscar ${field.label.toLowerCase()}…`}
          />
        ) : (
          <Select value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} {...common}>
            <option value="">—</option>
            {(field.options ?? []).map((o) => (
              <option key={o} value={o}>
                {field.name === "countryCode" ? (o === "CO" ? "Colombia" : "Perú") : o}
              </option>
            ))}
          </Select>
        )
      ) : (
        <Input
          type={inputType(field.type)}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          {...(field.min !== undefined ? { min: field.min } : {})}
          {...common}
        />
      )}
    </Field>
  );
}

function inputType(t: FieldType): string {
  switch (t) {
    case "email": return "email";
    case "phone": return "tel";
    case "url": return "url";
    case "int": case "currency": case "decimal": case "percent": return "number";
    case "date": return "date";
    default: return "text";
  }
}

type FieldType = FieldDef["type"];
